// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, OnInit } from '@angular/core';
import { HeaderService } from '../../header.service';
import { D3eService } from '../../../services/d3e-service';
import { Store } from '@ngrx/store';
import { AppState } from '../../store/app.state';
import { Observable, of } from 'rxjs';
import {
    AGENT_PRODUCT_PLAN_ID,
    BITLOCKER_PRODUCT_PLAN_ID,
    FDE_PRODUCT_PLAN_ID,
    HARDWARE2_PRODUCT_PLAN_ID,
    HARDWARE_PRODUCT_PLAN_ID,
    ISubscription
} from '../../../types/subscription';
import { TranslateService } from '@ngx-translate/core';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import { selectUser } from '../../store/user/user.selectors';
import { ALL_ORGS_ID, OrgType } from '../../../types/group';
import { sortSubscriptionsByPriority } from '../../../shared/subscription.methods';
import { UserService } from '../../../services/user-service';
import { Partners } from '../../../types/partners';
import { IManageUser } from '../../../types/user';

export interface IIncludedSoftware {
    productName: string;
    assignedLicenses: number;
    purchasedQuantity: number;
    licensesApply: boolean;
    canAssign: boolean;
    hasEndpoints: boolean;
    hiddenForPartners: Partners[];
}

type SubscriptionContext = {
    subscriptions: ISubscription[];
    organizationId: string;
}

@Component({
    selector: 'app-products',
    templateUrl: './products.component.html',
    styleUrls: ['./products.component.scss'],
    standalone: false
})
export class ProductsComponent implements OnInit {
  subscriptions$!: Observable<ISubscription[]>;
  isCustomerSelected$ = this.headerService.orgSelectedChanged.pipe(
    withLatestFrom(this.store.select(selectUser())),
    switchMap(([organizationId, user]: [string, IManageUser]) => {
      if (user?.org_type.value == OrgType.CUSTOMER) {
        return of(true);
      } else if (organizationId == ALL_ORGS_ID) {
        return of(false);
      } else {
        return this.userService.get_organization(organizationId).pipe(map(org => org?.org_type == OrgType.CUSTOMER));
      }
    })
  )

  public OrgType = OrgType;
  protected readonly BITLOCKER_PRODUCT_PLAN_ID = BITLOCKER_PRODUCT_PLAN_ID;
  protected readonly HARDWARE_PRODUCT_PLAN_ID = HARDWARE_PRODUCT_PLAN_ID;
  protected readonly HARDWARE2_PRODUCT_PLAN_ID = HARDWARE2_PRODUCT_PLAN_ID;
  protected readonly FDE_PRODUCT_PLAN_ID = FDE_PRODUCT_PLAN_ID;
  protected readonly AGENT_PRODUCT_PLAN_ID = AGENT_PRODUCT_PLAN_ID;

  subscriptionDataLoaded = false;

  constructor(
    private readonly headerService: HeaderService,
    private readonly d3eService: D3eService,
    private readonly userService: UserService,
    private readonly translate: TranslateService,
    private readonly store: Store<AppState>,
  ) {
    headerService.setContextHelp([
      'help.agent',
      'help.pba',
      'help.fde',
      'help.cli',
      'help.d3e'
    ]);
    headerService.setTitle(translate.instant('products.page-title'));
    headerService.setSubtitle('');
    headerService.setIcon('mdi-file-certificate-outline');
    headerService.setNavMenuText('');
    headerService.showUserFilter(true);
    headerService.setOrgSelectorDefaultOption(translate.instant('products.default-org-selector'));
  }

  ngOnInit() {
    this.subscriptions$ = this.headerService.orgSelectedChanged
      .pipe(
        switchMap(organizationId => {
          this.subscriptionDataLoaded = false;
          return this.d3eService.get_subscriptions(organizationId, '', true).pipe(
            map(subscriptions => ({subscriptions, organizationId}))
          );
        }),
        withLatestFrom(this.store.select(selectUser())),
        map(([{subscriptions, organizationId}, user]: [SubscriptionContext, IManageUser]) => {
          if (organizationId == ALL_ORGS_ID) {
            subscriptions = subscriptions.filter(subscription => {
              return subscription?.group_id == user?.group_id;
            });
          }
          this.subscriptionDataLoaded = true;
          return subscriptions.sort(sortSubscriptionsByPriority);
        })
      );
  }
}
